import {
  EXCLUDED_PROVIDER_COLOR,
  MARKET_PROVIDER_COLOR,
  MEMBERS_WITH_ACCESS_COLOR,
  MEMBERS_WITHOUT_ACCESS_COLOR,
  NETWORK_PROVIDER_COLOR,
  PROSPECT_PROVIDER_COLOR,
  TARGET_PROVIDER_COLOR,
  TERMINATED_PROVIDER_COLOR,
} from "@app/projects/network-assessment/maps/network-coverage-map/NetworkCoverageMapsConstants";
import { ProvidersFilterOptions } from "@app/projects/network-assessment/maps/network-coverage-map/enumerators";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { MultiMapExportContentOption, MultiMapExportMemberGroup, MultiMapExportMemberLayer, MultiMapExportProviderGroup } from "@app/projects/network-assessment/multi-map-export/interfaces";

/**
 * Swatch colors are imported, never copied, so a re-palette of the Network Coverage Map carries here
 * instead of drifting. Members are circles and providers are pins, matching the map's own legend.
 */
export const MAP_CONTENT_MEMBER_OPTIONS: readonly MultiMapExportContentOption<MultiMapExportMemberLayer>[] = Object.freeze([
  {
    label: "Members with Access",
    swatchColor: MEMBERS_WITH_ACCESS_COLOR,
    swatchIcon: QesIcons.Circle,
    value: MultiMapExportMemberLayer.WithAccess,
  },
  {
    label: "Members without Access",
    swatchColor: MEMBERS_WITHOUT_ACCESS_COLOR,
    swatchIcon: QesIcons.Circle,
    value: MultiMapExportMemberLayer.WithoutAccess,
  },
]);

/**
 * Render order, and the only place it is decided: Network, then Prospect, then Market. Prospect leads the
 * out-of-network column per providerNetworkTypeDisplayOrder, which the ADO copy's ordering does not follow.
 *
 * Targeted and Excluded intentionally share a color across Prospect and Market. The map has no per-source
 * variant: NetworkCoverageMap's icon_primary_color tests provider action status before network type, so a
 * targeted provider is magenta and an excluded one pale cyan whichever source it came from. The swatch shows
 * what the map will actually draw.
 */
/** Members are a group like any provider source, so the column gets the same parent select-all checkbox. */
export const MAP_CONTENT_MEMBER_GROUP: MultiMapExportMemberGroup = Object.freeze({
  key: "members",
  label: "Members",
  options: MAP_CONTENT_MEMBER_OPTIONS,
});

export const MAP_CONTENT_PROVIDER_GROUPS: readonly MultiMapExportProviderGroup[] = Object.freeze([
  {
    key: ProvidersFilterOptions.Network,
    label: "Network Providers",
    options: Object.freeze([
      {
        label: "Non-Terminated",
        swatchColor: NETWORK_PROVIDER_COLOR,
        swatchIcon: QesIcons.ProviderMapPin,
        value: ProvidersFilterOptions.NonTerminated,
      },
      {
        label: "Terminated",
        swatchColor: TERMINATED_PROVIDER_COLOR,
        swatchIcon: QesIcons.ProviderMapPin,
        value: ProvidersFilterOptions.Terminated,
      },
    ]),
  },
  {
    key: ProvidersFilterOptions.Prospect,
    label: "Prospect Providers",
    options: Object.freeze([
      {
        label: "Available",
        swatchColor: PROSPECT_PROVIDER_COLOR,
        swatchIcon: QesIcons.ProviderMapPin,
        value: ProvidersFilterOptions.AvailableProspect,
      },
      {
        label: "Targeted",
        swatchColor: TARGET_PROVIDER_COLOR,
        swatchIcon: QesIcons.ProviderMapPin,
        value: ProvidersFilterOptions.TargetedProspect,
      },
      {
        label: "Excluded",
        swatchColor: EXCLUDED_PROVIDER_COLOR,
        swatchIcon: QesIcons.ProviderMapPin,
        value: ProvidersFilterOptions.ExcludedProspect,
      },
    ]),
  },
  {
    key: ProvidersFilterOptions.Market,
    label: "Market Providers",
    options: Object.freeze([
      {
        label: "Available",
        swatchColor: MARKET_PROVIDER_COLOR,
        swatchIcon: QesIcons.ProviderMapPin,
        value: ProvidersFilterOptions.AvailableMarket,
      },
      {
        label: "Targeted",
        swatchColor: TARGET_PROVIDER_COLOR,
        swatchIcon: QesIcons.ProviderMapPin,
        value: ProvidersFilterOptions.TargetedMarket,
      },
      {
        label: "Excluded",
        swatchColor: EXCLUDED_PROVIDER_COLOR,
        swatchIcon: QesIcons.ProviderMapPin,
        value: ProvidersFilterOptions.ExcludedMarket,
      },
    ]),
  },
]);

/** Canonical stored order, so state never depends on the order the user clicked. */
export const MAP_CONTENT_PROVIDER_OPTION_ORDER: readonly ProvidersFilterOptions[] =
  Object.freeze(MAP_CONTENT_PROVIDER_GROUPS.flatMap((group) => group.options.map((option) => option.value)));

export const MAP_CONTENT_MEMBER_LAYER_ORDER: readonly MultiMapExportMemberLayer[] =
  Object.freeze(MAP_CONTENT_MEMBER_OPTIONS.map((option) => option.value));
